import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useVertical } from '../context/VerticalContext';

export interface RecurringExpense {
    id: string;
    org_id: string;
    vendor: string;
    description: string | null;
    category: string;
    amount: number;
    vat_amount: number;
    frequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    day_of_month: number | null;
    next_due_date: string;
    is_active: boolean;
    auto_post: boolean;
    last_posted_at: string | null;
    times_posted: number;
    notes: string | null;
    created_at: string;
}

export interface RecurringExpenseInput {
    vendor: string;
    description?: string;
    category?: string;
    amount: number;
    vat_amount?: number;
    frequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    day_of_month?: number;
    next_due_date: string;
    auto_post?: boolean;
    notes?: string;
}

export const useRecurringExpenses = () => {
    const [expenses, setExpenses] = useState<RecurringExpense[]>([]);
    const [loading, setLoading] = useState(true);
    const { activeOrgId } = useVertical();

    const fetchExpenses = useCallback(async () => {
        if (!activeOrgId) return;

        setLoading(true);
        const { data, error } = await supabase
            .from('recurring_expenses')
            .select('*')
            .eq('org_id', activeOrgId)
            .order('next_due_date', { ascending: true });

        if (error) {
            console.error('Error fetching recurring expenses:', error);
        } else {
            setExpenses(data || []);
        }
        setLoading(false);
    }, [activeOrgId]);

    const createExpense = async (input: RecurringExpenseInput) => {
        if (!activeOrgId) return { error: 'No org' };

        const { data, error } = await supabase
            .from('recurring_expenses')
            .insert({ ...input, org_id: activeOrgId })
            .select()
            .single();

        if (!error && data) {
            setExpenses(prev => [...prev, data]);
        }
        return { data, error };
    };

    const updateExpense = async (id: string, updates: Partial<RecurringExpenseInput>) => {
        const { data, error } = await supabase
            .from('recurring_expenses')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select()
            .single();

        if (!error && data) {
            setExpenses(prev => prev.map(e => e.id === id ? data : e));
        }
        return { data, error };
    };

    const deleteExpense = async (id: string) => {
        const { error } = await supabase
            .from('recurring_expenses')
            .delete()
            .eq('id', id);

        if (!error) {
            setExpenses(prev => prev.filter(e => e.id !== id));
        }
        return { error };
    };

    const toggleActive = async (id: string, isActive: boolean) => {
        return updateExpense(id, { is_active: isActive } as any);
    };

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    // Calculate totals
    const monthlyTotal = expenses
        .filter(e => e.is_active)
        .reduce((sum, e) => {
            switch (e.frequency) {
                case 'weekly': return sum + (e.amount * 4.33);
                case 'monthly': return sum + e.amount;
                case 'quarterly': return sum + (e.amount / 3);
                case 'yearly': return sum + (e.amount / 12);
                default: return sum;
            }
        }, 0);

    return {
        expenses,
        loading,
        monthlyTotal,
        createExpense,
        updateExpense,
        deleteExpense,
        toggleActive,
        refresh: fetchExpenses
    };
};
